import React, { useEffect, useCallback, useContext } from 'react';
import { toast } from 'react-toastify';
import { usePermission } from 'react-use';
import i18next from 'i18next';

import FcmTokenProvider from './FcmTokenProvider';
import ElectronFcmTokenProvider from './ElectronFcmTokenProvider';
import ModalContext from 'app/containers/ModalContext';
import { ModalTypes } from 'config';
import { isElectron } from 'utils/general';
import { ding } from 'utils/sounds';
import {
  isConfigUpdateMessage,
  isDriverArrivedMessage,
  isMenuUpdateMessage,
  isNewDeliveryMessage,
  isNewOrderMessage,
  isDriverAssignedMessage,
  isDriverUnassignedMessage,
  isCallWaiterMessage,
  fcmMessageValidators,
} from 'utils/firebase-messaging';
import type { NormalizedRestaurant } from 'types';
import { loadOrders } from 'store/orders';
import { loadRestaurant } from 'store/restaurant';
import { useAppDispatch, useAppSelector } from 'hooks/useRedux';
import { selectHasMultipleRestaurants, selectUserRestaurants } from 'store/app';
import PushNotificationPermissionDenied from './PushNotificationPermissionDenied';

let firebaseMessageListeners: ((message: any) => void)[] = [];

type FcmMessageHandlerProps = {
  messagingError: any;
  restaurant: NormalizedRestaurant | null;
  setFcmError: React.Dispatch<any>;
};

type CallWaiterMessageData = {
  table_name: string;
  subtype: 'menu' | 'order' | 'bill' | 'other';
  notes?: string;
};

const FcmMessageHandler: React.FC<FcmMessageHandlerProps> = ({
  messagingError,
  restaurant,
  setFcmError,
}) => {
  const dispatch = useAppDispatch();
  const { setModal } = useContext(ModalContext);
  const userRestaurants = useAppSelector(selectUserRestaurants);
  const hasMultipleRestaurants = useAppSelector(selectHasMultipleRestaurants);

  const notificationPermission = usePermission({ name: 'notifications' } as any);

  useEffect(() => {
    if (!isElectron() && notificationPermission === 'prompt' && 'Notification' in window) {
      Notification.requestPermission().catch((error) => {
        console.warn('Error requesting notification permission:', error);
      });
    }
  }, [notificationPermission]);

  const isPermissionDenied = !isElectron() && notificationPermission === 'denied';
  const isPermissionPrompt = !isElectron() && notificationPermission === 'prompt';

  const handleMenuUpdateMsg = useCallback((message) => {
    if (isMenuUpdateMessage(message)) {
      toast.info('Inny użytkownik zaktualizował menu, pobieram zmiany');
      dispatch(
        loadRestaurant(restaurant!.id, {
          allowCache: false,
          setLoading: true,
        })
      );
    }
  }, [dispatch, restaurant]);

  const handleConfigUpdateMsg = useCallback((message) => {
    if (isConfigUpdateMessage(message)) {
      toast.info('Inny użytkownik zaktualizował ustawienia, pobieram zmiany');
      dispatch(
        loadRestaurant(restaurant!.id, {
          allowCache: false,
          setLoading: true,
        })
      );
    }
  }, [dispatch, restaurant]);

  const handleDriverArrivedMsg = useCallback((message) => {
    if (isDriverArrivedMessage(message)) {
      toast.info('Dostawca przyjechał po odbiór zamówienia');
      ding();
    }
  }, []);

  const handleDriverAssignedMsg = useCallback((message) => {
    if (isDriverAssignedMessage(message)) {
      toast.info('Restauracja przydzieliła Ci nową dostawę');
      ding();
    }
  }, []);

  const handleDriverUnassignedMsg = useCallback((message) => {
    if (isDriverUnassignedMessage(message)) {
      toast.info(
        'Jedna z Twoich dostaw została przydzielona na nowo - sprawdź aktualną listę dostaw'
      );
      ding();
    }
  }, []);

  const handleNewDeliveryMsg = useCallback((message) => {
    if (isNewDeliveryMessage(message)) {
      toast.info('Otrzymałeś nową dostawę');
      ding();
    }
  }, []);

  const handleNewOrderMsg = useCallback((message) => {
    if (isNewOrderMessage(message)) {
      const ids =
        hasMultipleRestaurants && userRestaurants
          ? userRestaurants.map((r) => r.id).join(',')
          : restaurant!.id;
      const url = `/restaurants/${ids}/orders`;
      dispatch(loadOrders(url));
    }
  }, [dispatch, hasMultipleRestaurants, userRestaurants, restaurant]);

  const handleCallWaiterMsg = useCallback((message) => {
    if (isCallWaiterMessage(message)) {
      const { table_name, subtype, notes } = message.data as CallWaiterMessageData;
      const subtypeKey = ['menu', 'order', 'bill', 'other'].includes(subtype)
        ? subtype
        : 'other';
      setModal(
        {
          title: i18next.t('call-waiter.title'),
          text: (
            <>
              <div>{i18next.t('call-waiter.labels.table')}<strong>{table_name}</strong></div>
              <div>{i18next.t('call-waiter.labels.type')}<strong>{i18next.t(`call-waiter.subtypes.${subtypeKey}`)}</strong></div>
              {notes && (<div>{i18next.t('call-waiter.labels.notes')}<strong>{notes}</strong></div>)}
            </>
          ),
          confirm: () => {},
          confirmText: i18next.t('call-waiter.confirm'),
          confirmColor: 'success',
        },
        ModalTypes.CONFIRM
      );
      ding();
    }
  }, [setModal]);

  const handleFcmMessage = (message) => {
    firebaseMessageListeners.forEach((listener) => listener(message));
    const isUnknownMessage = fcmMessageValidators.every(
      (validator) => !validator(message)
    );
    if (isUnknownMessage) {
      ding();
      const content =
        'notification' in message
          ? `(testowe powiadomienie) ${message.notification.title}: ${message.notification.body}`
          : `Otrzymałeś testowe powiadomienie: ${JSON.stringify(message)}`;
      toast.info(content);
    }
  };

  useEffect(() => {
    firebaseMessageListeners = [
      handleConfigUpdateMsg,
      handleDriverArrivedMsg,
      handleMenuUpdateMsg,
      handleNewDeliveryMsg,
      handleNewOrderMsg,
      handleDriverAssignedMsg,
      handleDriverUnassignedMsg,
      handleCallWaiterMsg,
    ];
  }, [handleConfigUpdateMsg, handleDriverArrivedMsg, handleMenuUpdateMsg, handleNewDeliveryMsg, handleNewOrderMsg, handleDriverAssignedMsg, handleDriverUnassignedMsg, handleCallWaiterMsg]);

  if (isPermissionDenied) {
    return <PushNotificationPermissionDenied restaurant={restaurant} />;
  }

  if (isPermissionPrompt) {
    return null;
  }

  return (
    <>
      {isElectron() ? (
        <ElectronFcmTokenProvider
          messagingError={messagingError}
          onMessage={handleFcmMessage}
          setError={setFcmError}
          restaurant={restaurant as NormalizedRestaurant}
        />
      ) : (
        <FcmTokenProvider
          messagingError={messagingError}
          onMessage={handleFcmMessage}
          setError={setFcmError}
          restaurant={restaurant as NormalizedRestaurant}
        />
      )}
    </>
  );
};

export default FcmMessageHandler;
